'use client';
import { useId, useRef, useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import styles from './tabs.module.scss';
import useScrollable from '@/hooks/useScrollable';
import { Grid } from '../Grid/Grid';

export type TabItem = {
    title?: string;
    body: React.ReactNode | React.ReactNode[];
};

export interface TabsProps {
    items: TabItem[];
    defaultIndex?: number;
    isScrollContentToTop?: boolean;
    scrollContentToTopOffset?: number;
    id?: string;
    navLayout?: 'none' | 'grid-12' | 'grid-10';
}

export function Tabs({
    items,
    defaultIndex = 0,
    isScrollContentToTop = false,
    scrollContentToTopOffset = 0,
    id,
    navLayout = 'none',
}: TabsProps): React.JSX.Element {
    const tabId = useId();
    const [activeIndex, setActiveIndex] = useState(defaultIndex);
    const [focusIndex, setFocusIndex] = useState(defaultIndex);
    const [mountedIndexes, setMountedIndexes] = useState(() => new Set([defaultIndex]));

    const tabsRef = useRef<HTMLDivElement>(null);
    const tabsContentRef = useRef<HTMLDivElement>(null);

    const { scrollStart, scrolling, scrollEnd, centerScroll } = useScrollable(tabsRef);

    const [buttonRefs, setButtonRefs] = useState<(HTMLButtonElement | null)[]>([]);

    const scrollToContentTop = useCallback(() => {
        if (tabsContentRef.current) {
            tabsContentRef.current.scrollIntoView(true);
        }
    }, [tabsContentRef]);

    const selectTab = useCallback((index: number) => {
        setActiveIndex(index);
        setMountedIndexes((currentIndexes) =>
            currentIndexes.has(index) ? currentIndexes : new Set(currentIndexes).add(index),
        );
    }, []);

    const handleTabClick = useCallback(
        (index: number) => {
            selectTab(index);
            buttonRefs[index]?.focus();
            centerScroll(buttonRefs[index]);

            if (isScrollContentToTop) {
                scrollToContentTop();
            }
        },
        [buttonRefs, centerScroll, isScrollContentToTop, scrollToContentTop, selectTab],
    );

    function handleTabFocus(index: number) {
        if (activeIndex === index) {
            setFocusIndex(index);
        }
    }

    useEffect(() => {
        function hashChangeHandler() {
            if (window && window.location.hash) {
                for (let i = 0; i < items.length; i += 1) {
                    if (items[i].title) {
                        const tabClean = (items[i].title || '').toLowerCase().replace(/ /g, '');
                        const hashClean = window.location.hash.replace('#', '').toLowerCase().trim();

                        if (tabClean === hashClean) {
                            selectTab(i);
                            const scrollTop =
                                (buttonRefs[i]?.getBoundingClientRect().top || 0) -
                                document.body.getBoundingClientRect().top;
                            window.scrollTo(0, scrollTop - 70);
                            break;
                        }
                    }
                }
            }
        }

        window.addEventListener('hashchange', hashChangeHandler);
        hashChangeHandler();
        return () => window.removeEventListener('hashchange', hashChangeHandler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setButtonRefs((refs) =>
            Array(items.length)
                .fill(null)
                .map((_, i) => refs[i] || null),
        );
    }, [items.length]);

    useEffect(() => {
        const currentTabsRef = tabsRef.current;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft') {
                const nextIndex = focusIndex > 0 ? focusIndex - 1 : items.length - 1;
                buttonRefs[nextIndex]?.focus();
                setFocusIndex(nextIndex);
            } else if (event.key === 'ArrowRight') {
                const nextIndex = focusIndex < items.length - 1 ? focusIndex + 1 : 0;
                buttonRefs[nextIndex]?.focus();
                setFocusIndex(nextIndex);
            } else if (event.key === 'Home') {
                buttonRefs[0]?.focus();
                setFocusIndex(0);
            } else if (event.key === 'End') {
                buttonRefs[items.length - 1]?.focus();
                setFocusIndex(items.length - 1);
            }
        };

        currentTabsRef?.addEventListener('keydown', handleKeyDown);

        return () => {
            currentTabsRef?.removeEventListener('keydown', handleKeyDown);
        };
    }, [buttonRefs, focusIndex, items.length]);

    const wrapNav = (node: React.ReactNode) => {
        const content = (
            <div className={styles.tabsWrapper} {...(id ? { id } : {})}>
                {node}
            </div>
        );

        const gridProps =
            navLayout === 'grid-12'
                ? { col: { sm: 12 } as const }
                : navLayout === 'grid-10'
                  ? { col: { sm: 12, lg: 10 } as const, start: { lg: 2 } as const }
                  : null;

        if (!gridProps) return content;

        return (
            <Grid container>
                <Grid {...gridProps}>{content}</Grid>
            </Grid>
        );
    };

    return (
        <>
            {wrapNav(
                <div
                    ref={tabsRef}
                    className={styles.tabs}
                    role="tablist"
                    tabIndex={0}
                    aria-label="Tabs"
                    onTouchStart={scrollStart}
                    onTouchMove={scrolling}
                    onTouchEnd={scrollEnd}
                    onMouseDown={scrollStart}
                    onMouseMove={scrolling}
                    onMouseUp={scrollEnd}
                >
                    {items.map((item, i) => {
                        const tabsClassNames = cn(styles.tabsTitle, {
                            [styles.active]: activeIndex === i,
                        });

                        const parenthesisPosition = item.title?.indexOf('(');
                        const tabTitle =
                            parenthesisPosition && parenthesisPosition >= 0
                                ? item.title?.substring(0, parenthesisPosition)
                                : item.title;

                        return (
                            <button
                                ref={(el) => {
                                    buttonRefs[i] = el;
                                }}
                                key={i}
                                id={`${tabId}-tab-${i}`}
                                onClick={() => handleTabClick(i)}
                                onFocus={() => handleTabFocus(i)}
                                role="tab"
                                aria-selected={activeIndex === i}
                                aria-controls={`${tabId}-panel-${i}`}
                                className={tabsClassNames}
                                tabIndex={activeIndex === i ? 0 : -1}
                                data-cy="atoms-tabs-button"
                            >
                                {tabTitle}
                            </button>
                        );
                    })}
                </div>,
            )}

            <div
                className={styles.tabsContent}
                ref={tabsContentRef}
                style={{ scrollMarginTop: scrollContentToTopOffset }}
            >
                {items.map((item, i) => {
                    const contentClassNames = cn(styles.tabsPanel, {
                        [styles.active]: activeIndex === i,
                    });

                    return (
                        <div
                            className={contentClassNames}
                            key={i}
                            id={`${tabId}-panel-${i}`}
                            role="tabpanel"
                            aria-labelledby={`${tabId}-tab-${i}`}
                            data-cy="tabs-layout-tab-body"
                        >
                            {mountedIndexes.has(i) ? item.body : null}
                        </div>
                    );
                })}
            </div>
        </>
    );
}
